// ArbeitgeberAuswahl — der Arbeitgeber in der Kontakt-ANLAGE.
//
// Der Arbeitgeber ist ein Firmenkontakt, kein getippter Name.
// Ist die Firma noch nicht im System, wird sie per Schnellanlage (darunter links,
// ähnlich wie bei Vereinen) erfasst und beim Speichern angelegt.
//
// Anschrift und Branche stehen unter dem Namen, kommen aber AUS der Firmenakte
// und werden nicht mitgespeichert.

import * as React from "react";
import { makeStyles, shorthands } from "@fluentui/react-components";
import { REFINED } from "../risikoanalyseStep/tokens";
import { FirmaBranchenFeld } from "./ContactRefField";
import { KontaktName } from "../forms/KontaktSprung";
import { firmenZusatzZeilen, useFirmenAnzeige } from "./firmenAnzeige";
import type { ContactRef } from "./types";
import { apiUrl } from "../../utils/api";
import { useEntprellteSuche, holeJsonListe } from "../../hooks/useEntprellteSuche";
import { SuchStatus } from "../forms/widgets/SuchStatus";
import { FieldRow, LabeledInput } from "../forms/formPrimitives";
import { LegalFormSelect } from "../forms/widgets/LegalFormSelect";
import {
  fetchLegalFormOptions,
  rechtsformAusName,
  type LegalFormOption,
} from "../../utils/legalFormOptions";
import { PhoneInput } from "../forms/widgets/PhoneInput";
import { VollanlageWeiterKnopf } from "../forms/VollanlageWeiter";

const SearchIcon = (
  <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.8}>
    <circle cx="8.5" cy="8.5" r="5.5" />
    <path d="M13 13l4.5 4.5" />
  </svg>
);

const useStyles = makeStyles({
  fieldLabel: {
    fontFamily: REFINED.fontUI,
    fontWeight: 600,
    fontSize: "0.875rem",
    color: REFINED.ink,
  },
  searchWrap: {
    position: "relative",
    display: "flex",
    alignItems: "center",
  },
  searchInput: {
    width: "100%",
    height: "38px",
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: "34px",
    paddingRight: "12px",
    ...shorthands.borderRadius("10px"),
    ...shorthands.border("1px", "solid", REFINED.line),
    fontFamily: REFINED.fontUI,
    fontSize: "0.92rem",
    color: REFINED.ink,
    backgroundColor: REFINED.white,
    outlineStyle: "none",
    boxSizing: "border-box",
    ":focus": {
      ...shorthands.borderColor(REFINED.aub),
    },
  },
  searchIcon: {
    position: "absolute",
    left: "10px",
    display: "flex",
    alignItems: "center",
    color: REFINED.ink3,
    pointerEvents: "none",
  },
  searchMsg: {
    fontFamily: REFINED.fontUI,
    fontSize: "0.8rem",
    color: REFINED.ink3,
    marginTop: "2px",
  },
  resultsList: {
    position: "absolute",
    zIndex: 40,
    top: "calc(100% + 4px)",
    left: 0,
    right: 0,
    maxHeight: "220px",
    overflowY: "auto",
    backgroundColor: REFINED.white,
    ...shorthands.border("1px", "solid", REFINED.line),
    ...shorthands.borderRadius("10px"),
    boxShadow: REFINED.cardShadow,
    paddingTop: "4px",
    paddingBottom: "4px",
  },
  resultRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    paddingTop: "8px",
    paddingBottom: "8px",
    paddingLeft: "12px",
    paddingRight: "12px",
    ...shorthands.borderWidth("0"),
    ...shorthands.borderStyle("none"),
    backgroundColor: "transparent",
    cursor: "pointer",
    textAlign: "left",
    ":hover": { backgroundColor: REFINED.aub50 },
  },
  resultName: {
    fontFamily: REFINED.fontUI,
    fontSize: "0.9rem",
    fontWeight: 600,
    color: REFINED.ink,
  },
  resultType: {
    fontFamily: REFINED.fontUI,
    fontSize: "0.78rem",
    color: REFINED.ink3,
  },
  newLink: {
    backgroundColor: "transparent",
    ...shorthands.borderWidth("0"),
    ...shorthands.borderStyle("none"),
    color: REFINED.aub,
    fontFamily: REFINED.fontUI,
    fontSize: "0.85rem",
    fontWeight: 600,
    cursor: "pointer",
    paddingTop: "4px",
    paddingBottom: "4px",
    paddingLeft: 0,
    paddingRight: 0,
    textAlign: "left",
    alignSelf: "flex-start",
    ":hover": { textDecorationLine: "underline" },
  },
  switchLink: {
    backgroundColor: "transparent",
    ...shorthands.borderWidth("0"),
    ...shorthands.borderStyle("none"),
    color: REFINED.aub,
    fontFamily: REFINED.fontUI,
    fontSize: "0.82rem",
    fontWeight: 600,
    cursor: "pointer",
    paddingTop: "2px",
    paddingBottom: "2px",
    paddingLeft: 0,
    paddingRight: 0,
    textAlign: "left",
    ":hover": { textDecorationLine: "underline" },
  },
  chip: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    paddingTop: "7px",
    paddingBottom: "7px",
    paddingLeft: "12px",
    paddingRight: "12px",
    ...shorthands.border("1px", "solid", REFINED.line),
    ...shorthands.borderRadius("10px"),
    backgroundColor: REFINED.fieldBg,
    height: "38px",
    boxSizing: "border-box",
  },
  chipName: {
    fontFamily: REFINED.fontUI,
    fontWeight: 600,
    fontSize: "0.9rem",
    color: REFINED.ink,
  },
  chipChange: {
    backgroundColor: "transparent",
    ...shorthands.borderWidth("0"),
    ...shorthands.borderStyle("none"),
    color: REFINED.aub,
    fontWeight: 600,
    fontSize: "0.82rem",
    cursor: "pointer",
    ":hover": { textDecorationLine: "underline" },
  },
  hinweis: {
    fontFamily: REFINED.fontUI,
    fontSize: "0.8rem",
    lineHeight: "1.45",
    color: REFINED.ink2,
  },
  ghostBtn: {
    ...shorthands.border("1.5px", "solid", REFINED.aub),
    ...shorthands.borderRadius("10px"),
    backgroundColor: REFINED.white,
    color: REFINED.aub,
    fontFamily: REFINED.fontUI,
    fontWeight: 600,
    fontSize: "0.88rem",
    padding: "8px 16px",
    cursor: "pointer",
    transitionProperty: "background-color, color",
    transitionDuration: "140ms",
    ":hover": { backgroundColor: REFINED.aub50 },
    ":disabled": { opacity: 0.5, cursor: "not-allowed" },
  },
});

const displayNameOf = (c: any) =>
  c.displayName || c.name || [c.firstName, c.lastName].filter(Boolean).join(" ") || "Firma";

/**
 * Ort und Kontaktnummer unter dem Namen (T-333, Dirk 15.10.2026): Zwei
 * gleichnamige Firmen im Bestand standen hier als zwei identische Zeilen —
 * welche die richtige ist, war nicht zu erkennen.
 */
const trefferZusatz = (c: any): string =>
  [String(c?.defaultAddress?.city ?? "").trim(), c?.contactNbrInt ? `Nr. ${c.contactNbrInt}` : ""]
    .filter(Boolean)
    .join(" · ");

export function ArbeitgeberAuswahl({
  firmaId,
  name,
  onWaehlen,
  isNew,
  onStartNeuanlage,
  onCancelNeuanlage,
}: {
  firmaId?: string;
  name?: string;
  /**
   * `firma`: Rechtsform des Treffers — damit die Firma, die als „Verbindung
   * zu Unternehmen" gespiegelt wird, ihre Rechtsform gleich mitbringt.
   */
  onWaehlen: (id: number | null, name?: string, firma?: { legalForm?: string; legalFormId?: number }) => void;
  isNew?: boolean;
  onStartNeuanlage?: (initialName: string) => void;
  onCancelNeuanlage?: () => void;
}) {
  const s = useStyles();
  const [searchTerm, setSearchTerm] = React.useState("");
  const [openDropdown, setOpenDropdown] = React.useState(false);

  const id = Number(firmaId);
  const gewaehlt = Number.isFinite(id) && id > 0 ? id : undefined;
  const anzeige = useFirmenAnzeige(gewaehlt);

  // Entprellte Suche — Lade-Anzeige endet immer, Fehler sind sichtbar und
  // wiederholbar (useEntprellteSuche).
  const suche = useEntprellteSuche<any>(
    searchTerm,
    !gewaehlt && !isNew,
    async (term, signal) => {
      const list = await holeJsonListe(apiUrl("contact", {
        search: term,
        limit: "10",
        filters: JSON.stringify({ Typ: "Firma" }),
        // Firmensuche nur im Namen (nicht in E-Mail/Telefon/Adresse) und in der
        // schlanken Form ohne Verträge/Beziehungen — „court“ legte am 01.10.2026
        // mit der vollen Form PROD für 80 s lahm.
        nurName: "1",
        // Courté-weit: keine persönlichen Kontakte anderer Nutzer (auch nicht
        // für den admin) — eigene bleiben auffindbar.
        ohneFremdePersoenliche: "1",
        include: "",
      }), signal);
      return list.filter((c: any) => {
        // NUR nach Firmen suchen:
        const t = c?.type?.type || c?.type?.name || (typeof c?.type === "string" ? c.type : "");
        return String(t).trim().toLowerCase() === "firma";
      }).slice(0, 10);
    },
    { debounceMs: 280 },
  );
  const results = suche.treffer;

  return (
    <div style={{ display: "flex", flexDirection: "column", rowGap: "7px", position: "relative" }}>
      <label className={s.fieldLabel}>Arbeitgeber</label>
      {gewaehlt ? (
        <>
          <div className={s.chip}>
            <KontaktName contactId={gewaehlt} className={s.chipName}>
              {name || "Verknüpfte Firma"}
            </KontaktName>
            <button
              type="button"
              className={s.chipChange}
              onClick={() => {
                setSearchTerm("");
                onWaehlen(null);
              }}
            >
              ändern
            </button>
          </div>
          {firmenZusatzZeilen(anzeige).map((zeile) => (
            <span key={zeile} className={s.hinweis}>
              {zeile}
            </span>
          ))}
        </>
      ) : isNew ? (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", minHeight: "38px" }}>
          <span style={{ fontSize: "0.88rem", fontWeight: 600, color: REFINED.aub }}>
            Neuer Arbeitgeber (wird angelegt)
          </span>
          <button
            type="button"
            className={s.switchLink}
            onClick={() => {
              setSearchTerm("");
              onCancelNeuanlage?.();
            }}
          >
            ← stattdessen bestehenden Arbeitgeber suchen
          </button>
        </div>
      ) : (
        <>
          <div className={s.searchWrap}>
            <span className={s.searchIcon}>{SearchIcon}</span>
            <input
              type="text"
              className={s.searchInput}
              placeholder="Arbeitgeber suchen …"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setOpenDropdown(true);
              }}
              onFocus={() => {
                if (results.length > 0) setOpenDropdown(true);
              }}
              onBlur={() => {
                setTimeout(() => setOpenDropdown(false), 200);
              }}
            />
          </div>

          <SuchStatus suche={suche} term={searchTerm} className={s.searchMsg} anzahl={results.length} />

          {openDropdown && results.length > 0 && (
            <div className={s.resultsList}>
              {results.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className={s.resultRow}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    const firmenname = displayNameOf(c);
                    onWaehlen(c.id, firmenname, {
                      legalForm: c.legalForm?.legalForm || undefined,
                      legalFormId: Number(c.legalForm?.id) || undefined,
                    });
                    setSearchTerm(firmenname);
                    setOpenDropdown(false);
                  }}
                >
                  <span style={{ display: "flex", flexDirection: "column", rowGap: "1px" }}>
                    <span className={s.resultName}>{displayNameOf(c)}</span>
                    {trefferZusatz(c) && <span className={s.resultType}>{trefferZusatz(c)}</span>}
                  </span>
                  <span className={s.resultType}>{c.legalForm?.legalForm || "Firma"}</span>
                </button>
              ))}
            </div>
          )}

          <button
            type="button"
            className={s.newLink}
            onClick={() => {
              const initialName = searchTerm.trim();
              onStartNeuanlage?.(initialName);
              setOpenDropdown(false);
            }}
          >
            + Kontakt ist noch nicht im System — neu eintippen
          </button>
        </>
      )}
    </div>
  );
}

export function ArbeitgeberSchnellanlage({
  refData,
  onChange,
  onOpenBefuellen,
  onAngelegt,
}: {
  refData?: ContactRef;
  onChange: (patch: Partial<ContactRef>) => void;
  onOpenBefuellen: () => void;
  /**
   * Die Firma wurde über „+ Weitere Informationen erfassen" voll angelegt —
   * ab jetzt ist sie ein bestehender Arbeitgeber. Ohne Rückruf kein Knopf.
   */
  onAngelegt?: (contactId: number, ref: ContactRef) => void;
}) {
  const s = useStyles();
  const [legalFormOptions, setLegalFormOptions] = React.useState<LegalFormOption[]>([]);
  const legalFormTouchedRef = React.useRef(false);

  React.useEffect(() => {
    let active = true;
    fetchLegalFormOptions()
      .then((opts) => {
        if (active) setLegalFormOptions(opts);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  return (
    <div style={{ marginTop: "12px", display: "flex", flexDirection: "column", rowGap: "14px" }}>
      {/* Name | Rechtsform */}
      <FieldRow columns={2}>
        <LabeledInput
          label="Firmenname"
          name="arbeitgeber-firma-name"
          required
          value={refData?.lastName ?? ""}
          onChange={(v) => {
            const erkannt =
              !legalFormTouchedRef.current && legalFormOptions.length
                ? rechtsformAusName(v, legalFormOptions)
                : null;
            const hit =
              erkannt && erkannt.befund.konfidenz === "SICHER" ? erkannt.option : null;
            onChange({
              lastName: v,
              ...(hit ? { legalForm: hit.legalForm, legalFormId: hit.id } : {}),
            });
          }}
          placeholder="Muster GmbH"
        />
        <LegalFormSelect
          label="Rechtsform"
          name="arbeitgeber-legalform"
          required
          value={refData?.legalForm || ""}
          onChange={(v, id) => {
            legalFormTouchedRef.current = true;
            onChange({ legalForm: v, legalFormId: id ? Number(id) : undefined });
          }}
          freitext={refData?.rechtsform ?? ""}
          onFreitextChange={(v) => onChange({ rechtsform: v })}
        />
      </FieldRow>

      {/* Branche | Branchengruppen */}
      <FirmaBranchenFeld
        value={refData ?? { type: "Firma", manual: true }}
        onPatch={onChange}
        idBase="arbeitgeber-branche"
      />

      {/* Telefon | Mobil */}
      <FieldRow columns={2}>
        <PhoneInput actionButton={false} kind="landline" geschaeftlich
          label="Telefon"
          name="arbeitgeber-tel"
          value={refData?.phone ?? ""}
          onChange={(v) => onChange({ phone: v })}
        />
        <PhoneInput actionButton={false} kind="mobile" geschaeftlich
          label="Mobil"
          name="arbeitgeber-mob"
          value={refData?.mobile ?? ""}
          onChange={(v) => onChange({ mobile: v })}
        />
      </FieldRow>

      {/* E-Mail | Website */}
      <FieldRow columns={2}>
        <LabeledInput
          label="E-Mail"
          name="arbeitgeber-mail"
          value={refData?.email ?? ""}
          onChange={(v) => onChange({ email: v })}
        />
        <LabeledInput
          label="Website"
          name="arbeitgeber-web"
          value={refData?.web ?? ""}
          onChange={(v) => onChange({ web: v })}
          placeholder="https://…"
        />
      </FieldRow>

      {/* Daten befüllen Button rechts */}
      <FieldRow columns={2}>
        <div />
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button
            type="button"
            className={s.ghostBtn}
            disabled={!String(refData?.lastName ?? "").trim() && !String(refData?.web ?? "").trim()}
            onClick={onOpenBefuellen}
            title="Anschrift, Rechtsform und Rufnummern aus dem Impressum übernehmen"
          >
            Daten befüllen
          </button>
        </div>
      </FieldRow>

      {onAngelegt && (
        <VollanlageWeiterKnopf
          kontakt={() => ({ type: "Firma", manual: true, ...(refData ?? {}) })}
          onCreated={({ contactId, ref }) => onAngelegt(contactId, ref)}
        />
      )}
    </div>
  );
}

export default ArbeitgeberAuswahl;
